import React from "react";
import { useGetUsersQuery, useCountUsersQuery } from "../services/user.Service";
import Pagination from "../../../components/pagination";
import usePagination from "../../../customHooks/usePagination";
import { User } from "../types/user.entity";
import { UsersAutoSearch } from "./UsersAutoSearch";
import { Badge } from "./UsersBadge";
import { Link, useLocation } from "react-router-dom";
import { FaExternalLinkAlt, FaUsers, FaCheckCircle, FaTimesCircle, FaMoon, FaSun } from "react-icons/fa";
import { formatTimestampWithRelativeDate } from "../../../utils/date.util";
import { getLangNameByLangCode } from "src/utils/util";
import { UsersTableSkeleton } from "./UsersTableSkeleton";
import { useTheme } from "src/context/theme.context";
import { isUserSessionDisabled } from "src/utils/user-session.util";
import { UsersFilter } from "../services/user.Service";
import { appendSearchToPath, usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

type UsersRouteState = {
  userId?: string;
  name?: string;
  email?: string;
  phone?: string;
  organization?: string;
  project?: string;
  sessionDisabled?: "true";
};

const getRoleBadgeColor = (role: string, isDark: boolean) => {
  const darkMap: Record<string, string> = {
    ADMIN: "bg-red-900/20 text-red-300 border border-red-700/50",
    admin: "bg-red-900/20 text-red-300 border border-red-700/50",
    EMPLOYEE: "bg-blue-900/20 text-blue-300 border border-blue-700/50",
    employee: "bg-blue-900/20 text-blue-300 border border-blue-700/50",
    USER: "bg-emerald-900/20 text-emerald-300 border border-emerald-700/50",
    user: "bg-emerald-900/20 text-emerald-300 border border-emerald-700/50",
    LEARNER: "bg-purple-900/20 text-purple-300 border border-purple-700/50",
    learner: "bg-purple-900/20 text-purple-300 border border-purple-700/50",
    COACH: "bg-amber-900/20 text-amber-300 border border-amber-700/50",
    coach: "bg-amber-900/20 text-amber-300 border border-amber-700/50",
  };

  const lightMap: Record<string, string> = {
    ADMIN: "bg-slate-100 text-slate-700 border border-slate-200",
    admin: "bg-slate-100 text-slate-700 border border-slate-200",
    EMPLOYEE: "bg-blue-50 text-blue-700 border border-blue-200",
    employee: "bg-blue-50 text-blue-700 border border-blue-200",
    USER: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    user: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    LEARNER: "bg-purple-50 text-purple-700 border border-purple-200",
    learner: "bg-purple-50 text-purple-700 border border-purple-200",
    COACH: "bg-amber-50 text-amber-700 border border-amber-200",
    coach: "bg-amber-50 text-amber-700 border border-amber-200",
  };

  return isDark
    ? darkMap[role] || "bg-gray-800 text-gray-300 border border-gray-600"
    : lightMap[role] || "bg-gray-50 text-gray-700 border border-gray-200";
};

export const UsersList: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { search } = useLocation();
  const tableSearch = usePaginatedTableSearchParams<UsersRouteState>({
    parseState: (searchParams) => {
      const query: UsersRouteState = {};
      const userId = searchParams.get("userId");
      const name = searchParams.get("name");
      const email = searchParams.get("email");
      const phone = searchParams.get("phone");
      const organization = searchParams.get("organization");
      const project = searchParams.get("project");
      const sessionDisabled = searchParams.get("sessionDisabled");

      if (userId) query.userId = userId;
      if (name) query.name = name;
      if (email) query.email = email;
      if (phone) query.phone = phone;
      if (organization) query.organization = organization;
      if (project) query.project = project;
      if (sessionDisabled === "true") query.sessionDisabled = "true";

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.userId) nextParams.set("userId", state.userId);
      if (state.name) nextParams.set("name", state.name);
      if (state.email) nextParams.set("email", state.email);
      if (state.phone) nextParams.set("phone", state.phone);
      if (state.organization) nextParams.set("organization", state.organization);
      if (state.project) nextParams.set("project", state.project);
      if (state.sessionDisabled === "true") nextParams.set("sessionDisabled", "true");
      return nextParams;
    },
  });

  const routeState = tableSearch.state;
  const initialQuery = React.useMemo<UsersFilter>(
    () => ({
      ...(routeState.userId ? { userId: routeState.userId } : {}),
      ...(routeState.name ? { name: routeState.name } : {}),
      ...(routeState.email ? { email: routeState.email } : {}),
      ...(routeState.phone ? { "appProfile.phoneNumber": routeState.phone } : {}),
      ...(routeState.organization ? { organizationId: routeState.organization } : {}),
      ...(routeState.project ? { projectId: routeState.project } : {}),
      ...(routeState.sessionDisabled === "true" ? { isSessionTemporarilyDisabled: true } : {}),
    }),
    [
      routeState.email,
      routeState.name,
      routeState.organization,
      routeState.phone,
      routeState.project,
      routeState.sessionDisabled,
      routeState.userId,
    ]
  );

  const updateQuery = React.useCallback(
    (filters: UsersFilter) => {
      tableSearch.replaceFilters({
        userId: filters.userId,
        name: filters.name,
        email: filters.email,
        phone: filters["appProfile.phoneNumber"],
        organization: filters.organizationId,
        project: filters.projectId,
        sessionDisabled: filters.isSessionTemporarilyDisabled ? "true" : undefined,
      });
    },
    [tableSearch]
  );

  const { data, numberOfPages, currentPage, next, prev, isError, error, isLoading, isFetching } = usePagination<User>({
    useGetDataQuery: useGetUsersQuery,
    useCountData: useCountUsersQuery,
    paginationMode: "cursor",
    initQuery: initialQuery,
    initPageTokens: tableSearch.pageTokens,
    initCurrentIndex: tableSearch.currentIndex,
    syncKey: tableSearch.syncKey,
    onPaginationStateChange: tableSearch.replacePagination,
  });

  const { data: totalCount } = useCountUsersQuery({});

  const badgeBaseClasses = isDarkMode
    ? "rounded-full shadow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold tracking-wide"
    : "rounded-full shadow-sm ring-1 ring-inset ring-black/5 text-xs font-semibold tracking-wide";
  const showTableSkeleton = !data && (isLoading || isFetching);

  if (isError) {
    const rawErrorText = (() => {
      if (!error) return "Unknown error";
      // RTK Query error shapes: {data,status}, {error}, or generic
      const anyErr: any = error;
      if (typeof anyErr === "string") return anyErr;
      if (anyErr?.data) return String(anyErr.data);
      if (anyErr?.message) return String(anyErr.message);
      try { return JSON.stringify(anyErr); } catch { return String(anyErr); }
    })();

    const linkMatch = rawErrorText.match(/https?:\/\/[^\s)]+/);
    const indexLink = linkMatch ? linkMatch[0] : undefined;

    return (
      <div
        className={`text-red-500 p-6 border border-red-200 rounded-2xl max-w-4xl mx-auto shadow-lg ${
          isDarkMode ? "bg-red-900/20" : "bg-gradient-to-r from-red-50 to-pink-50"
        }`}
      >
        <h3 className="font-semibold text-lg mb-3">Error Loading Users</h3>
        <div className={`mb-4 text-sm ${isDarkMode ? "text-red-200" : "text-red-700"}`}>
          <pre className="whitespace-pre-wrap break-words">{rawErrorText}</pre>
        </div>
        {indexLink && (
          <div className="mb-4">
            <a
              href={indexLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-4 py-2 rounded-lg font-semibold transition-colors duration-200 ${
                isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              Open Firestore index creation link
            </a>
          </div>
        )}
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl hover:from-red-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`container mx-auto px-4 antialiased min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
      }`}
    >
      <div className="flex items-center justify-between mb-8">
        <h1
          className={`text-4xl font-extrabold tracking-tight ${
            isDarkMode
              ? "bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent"
              : "bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent"
          }`}
        >
          Users Management
        </h1>
      </div>

      <div
        className={`shadow-2xl rounded-2xl overflow-hidden border backdrop-blur-sm transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 border-blue-200/50"
        }`}
      >
        <div
          className={`px-8 py-6 border-b transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800"
              : "bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600"
          }`}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-4">
              <div>
                <span className={`text-xl font-bold ${isDarkMode ? "text-gray-100" : "text-white"}`}>
                  Users Overview
                </span>
                <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-emerald-100"}`}>
                  {totalCount || 0} total users • Page {currentPage} of {numberOfPages}
                </div>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              className={`p-3 rounded-xl transition-all duration-300 ${
                isDarkMode
                  ? "bg-gray-800 text-yellow-400 hover:bg-gray-700"
                  : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
              }`}
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDarkMode ? <FaSun className="w-5 h-5" /> : <FaMoon className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="mt-8 mb-6">
          <UsersAutoSearch updateQuery={updateQuery} initialQuery={initialQuery} />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y transition-colors duration-300">
            <thead
              className={`transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-700/80 divide-gray-600"
                  : "bg-gradient-to-r from-blue-50/80 to-indigo-50/80 divide-blue-200/30"
              }`}
            >
              <tr>
                {["Name & ID", "Email", "Roles", "Session", "Created At", "Actions"].map((header) => (
                  <th
                    key={header}
                    className={`px-8 py-5 text-center text-xs font-semibold uppercase tracking-wider border-b transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200 border-gray-600" : "text-blue-700 border-blue-200/50"
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody
              className={`divide-y transition-colors duration-300 ${
                isDarkMode ? "bg-gray-800/80 divide-gray-700" : "bg-white/80 divide-blue-100/50"
              }`}
            >
              {showTableSkeleton ? (
                <UsersTableSkeleton rows={10} isDarkMode={isDarkMode} />
              ) : data && data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-8 py-20 text-center">
                    <div className="text-center">
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                          isDarkMode
                            ? "bg-gradient-to-r from-blue-500 to-indigo-600"
                            : "bg-gradient-to-r from-blue-400 to-indigo-500"
                        }`}
                      >
                        <FaUsers className="w-10 h-10 text-white" />
                      </div>
                      <div className={`text-xl font-semibold mb-3 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                        No Users Found
                      </div>
                      <div className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-500"}`}>
                        Try adjusting your search criteria
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                data &&
                data.map((user, index) => (
                  <tr
                    key={user.id}
                    className={`transition-all duration-300 hover:transition-colors duration-300 ${
                      isDarkMode
                        ? `hover:bg-gray-700/80 ${index % 2 === 0 ? "bg-gray-800/60" : "bg-gray-700/30"}`
                        : `hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/80 ${
                            index % 2 === 0 ? "bg-white/60" : "bg-blue-50/30"
                          }`
                    }`}
                  >
                    <td className="px-8 py-5 whitespace-nowrap">
                      <div className="space-y-2">
                        <div className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                          {user.name}
                        </div>
                        <div
                          className={`max-w-xs overflow-hidden text-ellipsis font-mono text-xs tracking-wide px-3 py-2 rounded-lg border shadow-sm transition-colors duration-300 ${
                            isDarkMode
                              ? "text-blue-300 bg-blue-900/20 border-blue-700/50"
                              : "text-blue-600 bg-gradient-to-r from-blue-100 to-indigo-100 border-blue-200/50"
                          }`}
                        >
                          {user.id}
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5 whitespace-nowrap">
                      <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {user.email || "-"}
                      </div>
                    </td>
                    <td className="px-8 py-5 whitespace-nowrap">
                      {user.roles && user.roles.length > 0 ? (
                        <div className="flex flex-wrap gap-2 justify-center">
                          {user.roles.map((role, idx) => {
                            const isLearner = role.toLowerCase().includes("learner");
                            const isCoach = role.toLowerCase().includes("coach");
                            const language = isLearner
                              ? user.appMeta?.activeLearningLangCode
                              : isCoach
                                ? user.appMeta?.activeCoachingLangCode
                                : undefined;
                            const languageLabel = language ? getLangNameByLangCode(language) : "";

                            return (
                              <Badge
                                key={`${role}-${idx}`}
                                label={
                                  <>
                                    <span className="uppercase tracking-wide">{role}</span>
                                    {languageLabel && (
                                      <span className={isDarkMode ? "text-white/70" : "text-gray-600"}>
                                        <span className="mx-1">·</span>
                                        <span className="font-semibold">{languageLabel}</span>
                                      </span>
                                    )}
                                  </>
                                }
                                classes={`${getRoleBadgeColor(role, isDarkMode)} ${badgeBaseClasses} min-w-[60px] h-[28px] whitespace-nowrap transition-transform duration-200 hover:-translate-y-0.5`}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <span
                          className={`text-sm px-3 py-1 rounded-lg transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400 bg-gray-700" : "text-gray-400 bg-gray-100"
                          }`}
                        >
                          -
                        </span>
                      )}
                    </td>
                    <td className="px-8 py-5 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        {isUserSessionDisabled(user.appMeta) ? (
                          <div className="text-red-400" title="Session Disabled">
                            <FaTimesCircle className="w-6 h-6" />
                          </div>
                        ) : (
                          <div className="text-green-400" title="Session Active">
                            <FaCheckCircle className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-5 whitespace-nowrap">
                      <div
                        className={`text-sm px-4 py-2 rounded-xl border shadow-sm transition-colors duration-300 ${
                          isDarkMode
                            ? "text-gray-300 bg-purple-900/20 border-purple-700/50"
                            : "text-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200/50"
                        }`}
                      >
                        {formatTimestampWithRelativeDate(user.createdAt)}
                      </div>
                    </td>
                    <td className="px-8 py-5 whitespace-nowrap">
                      <Link
                        to={appendSearchToPath(`/users/${user.id}`, search)}
                        className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2"
                        title="View User Details"
                      >
                        <FaExternalLinkAlt />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          className={`px-8 py-6 border-t transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gray-700/60"
              : "border-blue-200/50 bg-gradient-to-r from-blue-50/60 to-indigo-50/60"
          }`}
        >
          <Pagination currentPage={currentPage} numberOfPages={numberOfPages || 1} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </div>
      </div>
    </div>
  );
};
